import { Link, useNavigate } from 'react-router-dom';
import { gql } from '@apollo/client';
import styled from 'styled-components';
import { Fragment } from 'react';
import { timeColumns } from '../../utils/columns';
import { asCheck } from '../../utils/formatters';
import { useQueryWithMsg } from '../../utils/hooks';
import { PaddedItemsTable } from '../tables/ItemsTable';
import { searchBy } from '../../utils/search';
import { alphanumericSort } from '../../utils/utils';

const SuperadminUsersQuery = gql`
  query UsersWithTeams {
    users {
      id
      email
      firstName
      lastName
      defaultUnitSystem
      superadminRole
      hasValidSubscription
      createdAt
      updatedAt
      teams {
        id
        name
      }
    }
  }
`;

const WrappedColumn = {
  maxWidth: '250px',
  overflow: 'hidden',
  wordWrap: 'break-word',
  whiteSpace: 'normal',
};

const SuperadminTeamLink = styled(({ id, name, className }) => {
  return (
    <Link
      to={`/superadmin/teams/${id}/members`}
      onClick={(e) => e.stopPropagation()}
      className={className}
    >
      {name}
    </Link>
  );
})`
  color: var(--text-color);
  &:hover {
    text-decoration: underline;
  }
`;

const TeamsCell = ({ teams }) => {
  const teamNames = [...teams].sort((a, b) => alphanumericSort(a.name, b.name));
  return (
    <>
      {teamNames.map(({ id, name }, i) => (
        <Fragment key={id}>
          {i > 0 && ', '}
          <SuperadminTeamLink id={id} name={name} />
        </Fragment>
      ))}
    </>
  );
};

const COLS = [
  {
    accessor: 'email',
    displayString: 'Email',
  },
  {
    accessor: 'firstName',
    displayString: 'First Name',
  },
  {
    accessor: 'lastName',
    displayString: 'Last Name',
  },
  {
    accessor: 'teams',
    displayString: 'Teams',
    format: (teams) => <TeamsCell teams={teams} />,
    style: WrappedColumn,
  },
  {
    accessor: 'superadminRole',
    displayString: 'Superadmin',
    format: asCheck,
    isSmall: true,
  },
  {
    accessor: 'hasValidSubscription',
    displayString: 'Subscription',
    format: asCheck,
    isSmall: true,
  },
  ...timeColumns,
];

const UsersTable = () => {
  const { msg, data } = useQueryWithMsg(SuperadminUsersQuery);
  const navigate = useNavigate();
  const getItems = (itemData) => {
    const { users } = itemData;
    return users.map((user) => ({
      ...user,
      onClick: () => navigate(`${user.id}/edit`),
    }));
  };

  return (
    <PaddedItemsTable
      data={data}
      msg={msg}
      cols={COLS}
      getItems={getItems}
      createButton="New User"
      createButtonOnClick={() => navigate('new')}
      hover
      searchable
      showCount
      search={searchBy(['email', 'firstName', 'lastName'])}
    />
  );
};

export default UsersTable;
